import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line, BarChart, Bar,
} from 'recharts';
import { Lock, Eye, Apple, Play, MousePointerClick, Globe, Smartphone, TrendingUp, LogOut } from 'lucide-react';
import Logo from '../components/Logo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PW_KEY = 'reset_admin_pw';
const RED = '#a83a4a';
const GREEN = '#16a34a';
const BLUE = '#3b82f6';

const fmt = (iso) => (iso ? `${iso.slice(5, 7)}/${iso.slice(8, 10)}` : '');
const axisStyle = { fontSize: 11, fill: 'rgba(255,255,255,0.4)' };
const tooltipStyle = {
  contentStyle: { background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 0, fontSize: 12, color: '#fff' },
  labelStyle: { color: 'rgba(255,255,255,0.5)' },
};

function Stat({ icon, label, value, sub, testid }) {
  return (
    <div className="p-5 bg-white/[0.025] border border-white/[0.06]" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3 text-white/40 text-[10px] uppercase tracking-[0.2em] font-body">
        {icon}<span>{label}</span>
      </div>
      <div className="font-heading text-3xl text-white">{value ?? '—'}</div>
      {sub !== undefined && sub !== null && <div className="text-white/30 text-xs mt-1 font-body">{sub}</div>}
    </div>
  );
}

function Chart({ title, children, height = 220 }) {
  return (
    <div className="p-5 bg-white/[0.025] border border-white/[0.06]">
      <p className="text-white/40 text-[10px] uppercase tracking-[0.2em] font-body mb-4">{title}</p>
      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">{children}</ResponsiveContainer>
      </div>
    </div>
  );
}

function ListPanel({ title, icon, items, keyName, testid }) {
  return (
    <div className="p-5 bg-white/[0.025] border border-white/[0.06]" data-testid={testid}>
      <p className="text-white/40 text-[10px] uppercase tracking-[0.2em] font-body mb-4 flex items-center gap-1.5">
        {icon}{title}
      </p>
      {items?.length ? (
        <div className="space-y-2 font-body text-sm">
          {items.map((i) => (
            <div key={i[keyName] || 'unknown'} className="flex justify-between">
              <span className="text-white/70 capitalize truncate pr-2">{i[keyName] || 'unknown'}</span>
              <span className="text-white/40 flex-shrink-0">{i.count}</span>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-white/30 text-sm font-body">No data yet.</p>
      )}
    </div>
  );
}

function LoginGate({ onSuccess }) {
  const [pw, setPw] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr('');
    try {
      await axios.post(`${API}/admin/marketing/login`, { password: pw });
      sessionStorage.setItem(PW_KEY, pw);
      onSuccess(pw);
    } catch (e2) {
      setErr(e2.response?.status === 401 ? 'Incorrect password.' : 'Could not reach the server.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#020202] flex items-center justify-center px-6">
      <form onSubmit={submit} className="w-full max-w-sm" data-testid="admin-login-form">
        <div className="flex justify-center mb-8"><Logo size={48} glow /></div>
        <p className="text-[#a83a4a] text-[10px] uppercase tracking-[0.3em] font-body text-center mb-2">Admin</p>
        <h1 className="font-heading text-2xl text-white text-center mb-8 tracking-tight">Marketing Dashboard</h1>

        <label className="block text-white/40 text-[10px] uppercase tracking-[0.2em] font-body mb-2">Password</label>
        <input
          type="password"
          autoFocus
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          className="w-full px-4 py-3 bg-white/[0.04] border border-white/10 text-white font-body text-sm focus:outline-none focus:border-[#a83a4a]/60 transition"
          data-testid="admin-password-input"
          placeholder="••••••••"
        />
        {err && <p className="text-red-400 text-xs mt-2 font-body" data-testid="admin-login-error">{err}</p>}
        <button
          type="submit"
          disabled={busy || !pw}
          className="mt-4 w-full px-5 py-3 bg-white text-black font-heading text-sm uppercase tracking-[0.2em] hover:bg-white/90 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          data-testid="admin-login-submit"
        >
          <Lock className="w-3.5 h-3.5" />
          {busy ? 'Unlocking…' : 'Unlock'}
        </button>
        <p className="text-white/25 text-[10px] font-body mt-6 text-center">
          This page is private. Set the password via <code className="text-white/40">MARKETING_ADMIN_PASSWORD</code> in backend env.
        </p>
      </form>
    </div>
  );
}

function Dashboard({ pw, onLogout }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [range, setRange] = useState(30);

  const fetchStats = useCallback(async (signal) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/admin/marketing/stats?days=${range}`, {
        headers: { 'X-Admin-Password': pw },
        signal,
      });
      setData(res.data);
    } catch (e) {
      if (axios.isCancel(e)) return;
      if (e.response?.status === 401) onLogout();
      else setError(e.response?.data?.detail || 'Failed to load stats');
    } finally {
      setLoading(false);
    }
  }, [pw, range, onLogout]);

  useEffect(() => {
    const ac = new AbortController();
    fetchStats(ac.signal);
    return () => ac.abort();
  }, [fetchStats]);

  const headline = data?.headline || {};
  const series = (data?.series || []).map((s) => ({ ...s, d: fmt(s.date) }));

  return (
    <div className="min-h-screen bg-[#020202] text-white">
      {/* Nav */}
      <nav className="border-b border-white/[0.06] bg-[#020202]/90 backdrop-blur-xl sticky top-0 z-50">
        <div className="container mx-auto max-w-6xl px-5 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <Logo size={28} />
            <span className="font-heading text-base text-white tracking-[0.25em]">ADMIN</span>
          </div>
          <div className="flex items-center gap-2" data-testid="range-picker">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                onClick={() => setRange(d)}
                className={`px-3 py-1.5 text-[10px] font-body uppercase tracking-[0.15em] border transition ${
                  range === d
                    ? 'bg-[#a83a4a]/20 border-[#a83a4a]/50 text-red-300'
                    : 'bg-white/[0.02] border-white/10 text-white/40 hover:text-white/60'
                }`}
                data-testid={`range-${d}`}
              >
                {d}d
              </button>
            ))}
            <button
              onClick={onLogout}
              className="ml-2 p-2 text-white/40 hover:text-white transition"
              title="Sign out"
              data-testid="admin-logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </nav>

      <main className="container mx-auto max-w-6xl px-5 py-8">
        <div className="mb-8">
          <h1 className="font-heading text-3xl md:text-4xl text-white mb-1 tracking-tight">Marketing Dashboard</h1>
          <p className="text-white/40 font-body text-sm">Traffic and store-button clicks across the marketing site.</p>
        </div>

        {loading && !data && <div className="text-white/40 font-body py-16 text-center" data-testid="admin-loading">Loading…</div>}
        {error && <div className="text-red-400 font-body py-16 text-center" data-testid="admin-error">{error}</div>}

        {data && (
          <>
            {/* Headline */}
            <section className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8" data-testid="headline-stats">
              <Stat icon={<Eye className="w-3 h-3" />} label="Pageviews" value={headline.pageviews} testid="stat-pageviews" />
              <Stat icon={<Apple className="w-3 h-3" />} label="App Store" value={headline.app_store_clicks} testid="stat-app-store" />
              <Stat icon={<Play className="w-3 h-3" />} label="Play Store" value={headline.play_store_clicks} testid="stat-play-store" />
              <Stat icon={<MousePointerClick className="w-3 h-3" />} label="Total clicks" value={headline.total_store_clicks} testid="stat-total-clicks" />
              <Stat icon={<TrendingUp className="w-3 h-3" />} label="CTR" value={`${headline.ctr_pct ?? 0}%`} sub="clicks / view" testid="stat-ctr" />
            </section>

            {/* Charts */}
            <section className="grid lg:grid-cols-2 gap-4 mb-8">
              <Chart title={`Pageviews (last ${range}d)`}>
                <AreaChart data={series}>
                  <defs>
                    <linearGradient id="gPv" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={RED} stopOpacity={0.55} />
                      <stop offset="100%" stopColor={RED} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                  <XAxis dataKey="d" tick={axisStyle} axisLine={false} tickLine={false} />
                  <YAxis tick={axisStyle} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip {...tooltipStyle} />
                  <Area type="monotone" dataKey="pageviews" stroke={RED} strokeWidth={2} fill="url(#gPv)" />
                </AreaChart>
              </Chart>

              <Chart title={`Store clicks (last ${range}d)`}>
                <LineChart data={series}>
                  <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                  <XAxis dataKey="d" tick={axisStyle} axisLine={false} tickLine={false} />
                  <YAxis tick={axisStyle} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip {...tooltipStyle} />
                  <Line type="monotone" dataKey="app_clicks" name="App Store" stroke={BLUE} strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="play_clicks" name="Play Store" stroke={GREEN} strokeWidth={2} dot={false} />
                </LineChart>
              </Chart>
            </section>

            <section className="grid lg:grid-cols-3 gap-4 mb-8">
              <ListPanel title="Top Buttons" icon={<MousePointerClick className="w-3 h-3" />} items={data.top_buttons} keyName="button" testid="top-buttons" />
              <ListPanel title="Traffic Sources" icon={<Globe className="w-3 h-3" />} items={data.sources} keyName="source" testid="sources" />
              <ListPanel title="Devices" icon={<Smartphone className="w-3 h-3" />} items={data.devices} keyName="device" testid="devices" />
            </section>

            <section className="grid lg:grid-cols-2 gap-4 mb-8">
              <Chart title="Pageviews by day (bars)">
                <BarChart data={series}>
                  <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                  <XAxis dataKey="d" tick={axisStyle} axisLine={false} tickLine={false} />
                  <YAxis tick={axisStyle} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip {...tooltipStyle} />
                  <Bar dataKey="pageviews" fill={RED} radius={[2, 2, 0, 0]} />
                </BarChart>
              </Chart>

              <ListPanel title="Top Pages" icon={<Eye className="w-3 h-3" />} items={data.top_pages} keyName="path" testid="top-pages" />
            </section>

            {data.countries?.length > 0 && (
              <section className="mb-8">
                <ListPanel title="Top Countries" icon={<Globe className="w-3 h-3" />} items={data.countries} keyName="country" testid="countries" />
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}

export default function AdminMarketing() {
  const [pw, setPw] = useState(() => sessionStorage.getItem(PW_KEY) || null);

  // Verify saved pw on mount — if backend rejects, log out
  useEffect(() => {
    if (!pw) return;
    let cancelled = false;
    (async () => {
      try {
        await axios.post(`${API}/admin/marketing/login`, { password: pw });
      } catch {
        if (!cancelled) {
          sessionStorage.removeItem(PW_KEY);
          setPw(null);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [pw]);

  const logout = () => {
    sessionStorage.removeItem(PW_KEY);
    setPw(null);
  };

  if (!pw) return <LoginGate onSuccess={setPw} />;
  return <Dashboard pw={pw} onLogout={logout} />;
}
